'use client';

import type { ReactNode } from 'react';
import { createContext, useContext, useMemo, useCallback } from 'react';
import type { ToolExecutionHar } from '@terra/shared-contracts-tracker';
import type { WebGrpcDecodedData } from '@terra/shared-schemas-tracker';
import {
  enrichToolHarEntryForDisplay,
  type StoredHarDisplayEntry,
} from '@terra/shared-utils';

type EnrichHarEntryForDisplay = (params: {
  har: ToolExecutionHar;
}) => Promise<StoredHarDisplayEntry>;

export type HarDisplayConfig = {
  applicationId?: string;
  protocolDecodeEnabled?: boolean;
  decodeHarPreview?: (params: {
    applicationId: string;
    request: StoredHarDisplayEntry['request'];
    response: StoredHarDisplayEntry['response'];
  }) => Promise<WebGrpcDecodedData | null>;
};

export type FetchToolCallHars = (params: {
  threadId: string;
  toolCallId: string;
}) => Promise<ToolExecutionHar[]>;

type ChatHarContextType = {
  fetchHarData: (toolCallId: string) => Promise<ToolExecutionHar[]>;
  fetchHarById?: (id: string) => Promise<ToolExecutionHar | null>;
  hasHarPresence?: (toolCallId: string) => boolean;
  enrichHarEntryForDisplay?: EnrichHarEntryForDisplay;
  protocolDecodeEnabled?: boolean;
};

const ChatHarContext = createContext<ChatHarContextType | undefined>(undefined);

type ChatHarProviderProps = {
  children: ReactNode;
  threadId: string | null;
  fetchHarData: FetchToolCallHars;
  fetchHarById?: (id: string) => Promise<ToolExecutionHar | null>;
  hasHarPresence?: (toolCallId: string) => boolean;
  harDisplay?: HarDisplayConfig;
};

const buildEnrichHarEntryForDisplay = ({
  harDisplay,
}: {
  harDisplay?: HarDisplayConfig;
}): EnrichHarEntryForDisplay | undefined => {
  if (!harDisplay?.decodeHarPreview) {
    return undefined;
  }

  const {
    applicationId,
    protocolDecodeEnabled = true,
    decodeHarPreview,
  } = harDisplay;

  return ({ har }) =>
    enrichToolHarEntryForDisplay({
      har,
      isGrpcWebSupportEnabled: protocolDecodeEnabled,
      decodeGrpcHarPreview: ({
        applicationId: harApplicationId,
        request,
        response,
      }) =>
        decodeHarPreview({
          applicationId: harApplicationId ?? applicationId ?? '',
          request,
          response,
        }),
    });
};

export function ChatHarProvider({
  children,
  threadId,
  fetchHarData: fetchHarDataProp,
  fetchHarById: fetchHarByIdProp,
  hasHarPresence: hasHarPresenceProp,
  harDisplay,
}: ChatHarProviderProps) {
  const fetchHarData = useCallback(
    async (toolCallId: string): Promise<ToolExecutionHar[]> =>
      threadId === null ? [] : fetchHarDataProp({ threadId, toolCallId }),
    [fetchHarDataProp, threadId],
  );

  const fetchHarById = useCallback(
    async (id: string) => {
      if (!fetchHarByIdProp) {
        console.warn('fetchHarById not provided to ChatHarProvider');

        return null;
      }

      return fetchHarByIdProp(id);
    },
    [fetchHarByIdProp],
  );

  const enrichHarEntryForDisplay = useMemo(
    () => buildEnrichHarEntryForDisplay({ harDisplay }),
    [harDisplay],
  );

  const contextValue: ChatHarContextType = useMemo(
    () => ({
      fetchHarData,
      fetchHarById: fetchHarByIdProp ? fetchHarById : undefined,
      hasHarPresence: hasHarPresenceProp,
      enrichHarEntryForDisplay,
      protocolDecodeEnabled: harDisplay?.protocolDecodeEnabled ?? true,
    }),
    [
      fetchHarData,
      fetchHarById,
      fetchHarByIdProp,
      hasHarPresenceProp,
      enrichHarEntryForDisplay,
      harDisplay?.protocolDecodeEnabled,
    ],
  );

  return (
    <ChatHarContext.Provider value={contextValue}>
      {children}
    </ChatHarContext.Provider>
  );
}

export function useChatHar() {
  const context = useContext(ChatHarContext);

  if (context === undefined) {
    throw new Error('useChatHar must be used within a ChatHarProvider');
  }

  return context;
}

export function useChatHarOptional(): ChatHarContextType | undefined {
  return useContext(ChatHarContext);
}
